import { useState, useCallback, useMemo, useContext } from 'react';
import Button from '@wix/da-shared-react/pkg/Button';
import { useTranslation } from 'react-i18next';
import {
  CommissionStatusType,
  CommissionStatusCodeMap,
} from '@wix/da-shared-react/pkg/types/commissions';
import {
  default as CommissionConfirmation,
  Props as CommissionConfirmationProps,
} from '@wix/da-shared-react/pkg/CommissionEditPopup/CommissionConfirmation';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import DisabledOverlay from '../../_partials/DisabledOverlay';
import DisabledEmptyState from '../../_partials/DisabledEmptyState';
import LockIcon from '@wix/da-ds/pkg/Icons/24x24/Lock';
import UnlockIcon from '@wix/da-ds/pkg/Icons/24x24/Unlock';
import CommissionCreateButton from '../../_partials/CommissionCreateButton';
import { PapiDeviation } from '@wix/da-papi-types';

import s from './ShopCommissionsControls.scss';

export interface Props {
  className?: string;
  isOpenForCommissions: boolean;
  openCommissionEditModal(): void;
  changeAllCommissionsStatus(status: CommissionStatusType): void;
  isOwner: boolean;
  total: number;
  items?: PapiDeviation[];
}

function ShopCommissionsControls(props: Props) {
  const { t } = useTranslation();
  const [confirmationPopupType, setConfirmationPopupType] =
    useState<CommissionConfirmationProps['confirmationType']>(undefined);
  const isMobile = useContext(MobileContext);

  const {
    isOwner,
    total,
    isOpenForCommissions,
    items = [],
    changeAllCommissionsStatus,
  } = props;

  const { showChangeStatusButton, allCommissionsDisabled } = useMemo(() => {
    const activeCommissionsCount = items.filter(
      deviation =>
        deviation.commission?.status ===
        CommissionStatusCodeMap[CommissionStatusType.ACTIVE]
    ).length;
    return {
      showChangeStatusButton: total > 1,
      allCommissionsDisabled: activeCommissionsCount === 0,
    };
  }, [items, total]);

  const openChangeStatusConfirmation = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      setConfirmationPopupType(
        allCommissionsDisabled ? 'enableAll' : 'disableAll'
      );
    },
    [allCommissionsDisabled]
  );

  const closeChangeStatusConfirmation = useCallback(() => {
    setConfirmationPopupType(undefined);
  }, []);

  const onChangeStatus = useCallback(() => {
    const status = allCommissionsDisabled
      ? CommissionStatusType.ACTIVE
      : CommissionStatusType.DISABLED;
    changeAllCommissionsStatus(status);
    closeChangeStatusConfirmation();
  }, [
    allCommissionsDisabled,
    changeAllCommissionsStatus,
    closeChangeStatusConfirmation,
  ]);

  const renderDisabledView = () =>
    isOwner && !isMobile ? (
      <DisabledOverlay className={s['disabled-overlay']} />
    ) : (
      <DisabledEmptyState view="large" />
    );

  if (!total) {
    return <CommissionCreateButton />;
  }

  if (!isOpenForCommissions) {
    return renderDisabledView();
  }

  return (
    isOwner &&
    !isMobile && (
      <div className={s['owner-controls']}>
        {showChangeStatusButton && (
          <Button
            className={s['button']}
            view="b3"
            onClick={openChangeStatusConfirmation}
          >
            {allCommissionsDisabled ? (
              <UnlockIcon className={s['icon-unlock']} />
            ) : (
              <LockIcon className={s['icon-lock']} />
            )}
            {allCommissionsDisabled
              ? t('common.openAll')
              : t('common.closeAll')}
          </Button>
        )}
        {confirmationPopupType && (
          <CommissionConfirmation
            isOpen
            confirmationType={confirmationPopupType}
            onCancel={closeChangeStatusConfirmation}
            onConfirm={onChangeStatus}
          />
        )}
      </div>
    )
  );
}

export default ShopCommissionsControls;
